import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  MessageSquare,
  Search,
  Eye,
  Mail,
  Send,
  CheckCircle,
  Clock,
  RotateCcw,
  User,
  Phone,
  Calendar,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";

import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import loader from "../../../methods/loader";
import FormControl from "../../../components/common/FormControl";
import { MODULE_CONFIG, TenantInquiryService } from "./shared";

export default function ContactUsList() {
  const navigate = useNavigate();

  // Listing state
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters & Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Quick Reply Modal state
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  // Search input handler with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch paginated inquiries strictly from API
  const fetchInquiries = useCallback(
    async (showLoader = false) => {
      if (showLoader) loader(true);
      setLoading(true);

      try {
        const res = await TenantInquiryService.getListing({
          page,
          limit,
          status: statusFilter,
          search: debouncedSearch,
        });

        if (res?.success) {
          const raw = res?.data;
          const records = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.docs)
            ? raw.docs
            : Array.isArray(raw?.result)
            ? raw.result
            : [];
          setItems(records);
          const total =
            res?.total ??
            res?.totalDocs ??
            res?.count ??
            raw?.total ??
            raw?.totalDocs ??
            raw?.count ??
            records.length;
          setTotalCount(Number(total) || 0);
        } else {
          setItems([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error("Failed to fetch inquiries:", err);
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        if (showLoader) loader(false);
      }
    },
    [page, limit, statusFilter, debouncedSearch]
  );

  useEffect(() => {
    fetchInquiries(true);
  }, [fetchInquiries]);

  // Toggle Read / Unread Status
  const handleToggleStatus = async (item) => {
    const id = item?.id || item?._id;
    const nextStatus = item?.status === "read" ? "unread" : "read";
    loader(true);

    try {
      const res = await TenantInquiryService.changeStatus({
        id,
        status: nextStatus,
      });

      if (res?.success) {
        toast.success(`Inquiry marked as ${nextStatus}.`);
        setItems((prev) =>
          prev.map((it) => ((it.id || it._id) === id ? { ...it, status: nextStatus } : it))
        );
      } else {
        toast.error("Failed to update status.");
      }
    } catch (err) {
      console.error("Toggle status error:", err);
      toast.error("Failed to update status.");
    } finally {
      loader(false);
    }
  };

  // Open Quick Reply Modal
  const openReplyModal = (item) => {
    setSelectedInquiry(item);
    const customerName =
      item?.fullName || item?.name || (item?.email ? item.email.split("@")[0] : "Customer");
    setReplySubject(
      item?.subject ? `Re: ${item.subject}` : `Re: Inquiry from ${customerName}`
    );
    setReplyMessage("");
    setReplyModalOpen(true);
  };

  // Send Direct Reply
  const handleSendReply = async (e) => {
    if (e) e.preventDefault();
    if (!replyMessage.trim()) {
      toast.error("Please enter a reply message.");
      return;
    }

    const id = selectedInquiry?.id || selectedInquiry?._id;
    setSendingReply(true);
    loader(true);

    try {
      const res = await TenantInquiryService.sendReply({
        contactUsIds: [id],
        subject: replySubject.trim(),
        message: replyMessage.trim(),
      });

      if (res?.success) {
        toast.success("Reply email sent successfully to inquiring customer!");
        setReplyModalOpen(false);
        // Optimistically update status to replied
        setItems((prev) =>
          prev.map((it) => ((it.id || it._id) === id ? { ...it, status: "replied" } : it))
        );
      } else {
        toast.error(res?.message || "Failed to send reply email.");
      }
    } catch (err) {
      console.error("Reply error:", err);
      toast.error("Failed to send reply.");
    } finally {
      setSendingReply(false);
      loader(false);
    }
  };

  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F7FB] antialiased text-slate-800">
      <Header />

      {/* Main Container with correct spacing below fixed navbar and above floating dock */}
      <div className="min-h-[calc(100vh_-_60px)] md:min-h-[calc(100vh_-_70px)] mt-[60px] lg:mt-[70px] py-6 lg:py-8">
        <div className="container mx-auto px-3 sm:px-6 lg:px-8 max-w-5xl space-y-6 pb-36">

          {/* ─── 1. PAGE TITLE & DESCRIPTION ─── */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                {MODULE_CONFIG.title}
              </h1>
              <p className="text-xs sm:text-sm text-slate-600 mt-1">
                {MODULE_CONFIG.subtitle}
              </p>
            </div>

            {/* Total Inquiries Count Badge */}
            <div className="self-start sm:self-auto bg-white border border-slate-200/90 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-xs flex items-center gap-2">
              <MessageSquare size={15} className="text-[#115B9D]" />
              <span>
                Total: <strong className="text-slate-900 font-bold">{totalCount}</strong>
              </span>
            </div>
          </div>

          {/* ─── 2. FILTERS & SEARCH ROW ─── */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3.5 sm:p-4 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Search Input */}
            <div className="relative w-full sm:w-80 group">
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-focus-within:text-[#115B9D] transition-colors" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by customer name, email, or subject..."
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-8 py-2 text-xs sm:text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#115B9D] focus:bg-white focus:ring-1 focus:ring-[#115B9D] transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Status Tabs */}
            <div className="flex items-center gap-1.5 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0 scrollbar-none justify-start sm:justify-end">
              {MODULE_CONFIG.statusOptions.map((opt) => {
                const isActive = statusFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setStatusFilter(opt.value);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                      isActive
                        ? "bg-[#115B9D] text-white shadow-xs"
                        : "bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}

              {(searchQuery || statusFilter !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setStatusFilter("all");
                    setPage(1);
                  }}
                  className="p-1.5 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 border border-slate-200 transition-colors"
                  title="Reset filters"
                >
                  <RotateCcw size={14} />
                </button>
              )}
            </div>
          </div>

          {/* ─── 3. INQUIRIES LISTING (TABLE & CARDS) ─── */}
          <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 shadow-xs overflow-hidden">
            {loading ? (
              <div className="p-16 text-center space-y-3">
                <div className="inline-block w-7 h-7 border-3 border-slate-200 border-t-[#115B9D] rounded-full animate-spin" />
                <p className="text-xs font-bold text-slate-600">Loading inquiries from server...</p>
              </div>
            ) : items.length === 0 ? (
              <div className="p-16 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 text-[#115B9D] flex items-center justify-center mx-auto">
                  <MessageSquare size={22} />
                </div>
                <h3 className="text-sm font-bold text-slate-900">No Inquiries Found</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  {searchQuery || statusFilter !== "all"
                    ? "No customer inquiries matched your active search or status filter."
                    : "When customers submit contact inquiries through your quoting portal, they will appear here."}
                </p>
                {(searchQuery || statusFilter !== "all") && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setStatusFilter("all");
                      setPage(1);
                    }}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200"
                  >
                    <RotateCcw size={13} />
                    <span>Clear Filters</span>
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/70 text-slate-600 font-bold uppercase tracking-wider text-[11px]">
                        <th className="py-3 px-4">Customer</th>
                        <th className="py-3 px-4">Inquiry / Subject</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4">Date</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((item) => {
                        const id = item.id || item._id;
                        const customerName =
                          item.fullName || item.name || (item.email ? item.email.split("@")[0] : "Customer");
                        const statusConfig = MODULE_CONFIG.statusMap[item.status] || {
                          label: item.status || "unread",
                          bgClass: "bg-slate-100 text-slate-700 border-slate-200",
                          dotClass: "bg-slate-400",
                        };

                        return (
                          <tr
                            key={id}
                            className={`hover:bg-slate-50/80 transition-colors ${
                              item.status === "unread" ? "bg-amber-50/20 font-semibold" : ""
                            }`}
                          >
                            {/* Customer column */}
                            <td className="py-3.5 px-4">
                              <div className="font-bold text-slate-900 text-xs sm:text-sm">
                                {customerName}
                              </div>
                              <div className="text-[11px] text-slate-500 font-normal truncate max-w-[180px]">
                                {item.email || "—"}
                              </div>
                              {item.mobileNo || item.phone ? (
                                <div className="text-[11px] text-slate-400 font-normal">
                                  {item.mobileNo || item.phone}
                                </div>
                              ) : null}
                            </td>

                            {/* Subject & snippet */}
                            <td className="py-3.5 px-4 max-w-xs">
                              <div
                                onClick={() => navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)}
                                className="font-bold text-slate-900 hover:text-[#115B9D] cursor-pointer truncate transition-colors"
                              >
                                {item.subject || "No Subject"}
                              </div>
                              <p className="text-[11px] text-slate-500 font-normal line-clamp-1 mt-0.5">
                                {item.message || "—"}
                              </p>
                            </td>

                            {/* Status */}
                            <td className="py-3.5 px-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${statusConfig.bgClass}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotClass}`} />
                                {statusConfig.label}
                              </span>
                            </td>

                            {/* Date */}
                            <td className="py-3.5 px-4 whitespace-nowrap text-slate-500 font-normal text-[11px]">
                              {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"}
                            </td>

                            {/* Actions */}
                            <td className="py-3.5 px-4 text-right whitespace-nowrap">
                              <div className="inline-flex items-center gap-1.5 justify-end">
                                <button
                                  type="button"
                                  onClick={() => navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)}
                                  className="p-1.5 rounded-lg text-slate-600 hover:text-[#115B9D] hover:bg-slate-100 transition-colors"
                                  title="View inquiry dossier"
                                >
                                  <Eye size={15} />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => openReplyModal(item)}
                                  className="p-1.5 rounded-lg text-[#115B9D] hover:bg-blue-50 transition-colors"
                                  title="Send email reply"
                                >
                                  <Send size={14} />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleToggleStatus(item)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                                  title={item.status === "read" ? "Mark unread" : "Mark read"}
                                >
                                  <CheckCircle size={15} className={item.status === "read" ? "text-emerald-600" : ""} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards View */}
                <div className="block md:hidden divide-y divide-slate-100">
                  {items.map((item) => {
                    const id = item.id || item._id;
                    const customerName =
                      item.fullName || item.name || (item.email ? item.email.split("@")[0] : "Customer");
                    const statusConfig = MODULE_CONFIG.statusMap[item.status] || {
                      label: item.status || "unread",
                      bgClass: "bg-slate-100 text-slate-700 border-slate-200",
                      dotClass: "bg-slate-400",
                    };

                    return (
                      <div key={id} className="p-4 space-y-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusConfig.bgClass}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotClass}`} />
                            {statusConfig.label}
                          </span>
                          <span className="text-[11px] text-slate-400">
                            {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ""}
                          </span>
                        </div>

                        <div>
                          <h4
                            onClick={() => navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)}
                            className="font-bold text-sm text-slate-900 hover:text-[#115B9D] cursor-pointer"
                          >
                            {item.subject || "No Subject"}
                          </h4>
                          <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                            {item.message || "—"}
                          </p>
                        </div>

                        <div className="text-xs text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="font-semibold text-slate-900">{customerName}</span>
                          {item.email && <span className="text-slate-500 text-[11px]">{item.email}</span>}
                        </div>

                        <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-50">
                          <button
                            type="button"
                            onClick={() => navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)}
                            className="px-3 py-1.5 rounded-xl text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => openReplyModal(item)}
                            className="px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-[#115B9D] hover:bg-[#0e4b82] flex items-center gap-1"
                          >
                            <Send size={12} />
                            <span>Reply</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination Footer */}
                {totalPages > 1 && (
                  <div className="p-4 border-t border-slate-200 flex items-center justify-between gap-2 text-xs">
                    <span className="text-slate-500 font-medium">
                      Page {page} of {totalPages} ({totalCount} items)
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className={`p-1.5 rounded-lg border border-slate-200 ${
                          page <= 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-50 text-slate-700"
                        }`}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        type="button"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        className={`p-1.5 rounded-lg border border-slate-200 ${
                          page >= totalPages ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-50 text-slate-700"
                        }`}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── 4. QUICK REPLY MODAL (z-[99999] ABOVE NAVBAR) ─── */}
      {replyModalOpen && selectedInquiry && (
        <div className="fixed inset-0 z-[99999] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200 w-full max-w-lg my-auto flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3 bg-slate-50">
              <div className="flex items-center gap-2">
                <span className="p-1.5 rounded-lg bg-[#115B9D]/10 text-[#115B9D]">
                  <Send size={15} />
                </span>
                <div>
                  <h3 className="font-extrabold text-sm sm:text-base text-slate-900">
                    Reply to Customer
                  </h3>
                  <p className="text-xs text-slate-500">
                    To: {selectedInquiry.email || selectedInquiry.fullName || "Customer"}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setReplyModalOpen(false)}
                className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-900 flex items-center justify-center"
              >
                <X size={15} />
              </button>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSendReply} className="p-5 space-y-4">
              <div>
                <FormControl
                  type="text"
                  label="Subject"
                  placeholder="Reply subject line..."
                  value={replySubject}
                  onChange={(val) => setReplySubject(val)}
                  required
                />
              </div>

              <div>
                <FormControl
                  type="textarea"
                  rows={5}
                  label="Message Body"
                  placeholder="Type your response to the customer..."
                  value={replyMessage}
                  onChange={(val) => setReplyMessage(val)}
                  required
                />
              </div>

              {/* Original Inquiry Snippet */}
              {selectedInquiry.message && (
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Original Customer Inquiry:
                  </span>
                  <p className="text-slate-600 line-clamp-2 italic">
                    "{selectedInquiry.message}"
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="pt-2 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setReplyModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingReply || !replyMessage.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#115B9D] hover:bg-[#0e4b82] shadow-xs disabled:opacity-50"
                >
                  <Send size={13} />
                  <span>{sendingReply ? "Sending..." : "Send Reply Email"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
